/**
 * Shift Management Component
 * Admin/HR page to manage shift schedules (CRUD operations)
 * 
 * Features:
 * - List all shifts with filtering (active/inactive)
 * - Create new shift schedules
 * - Edit existing shifts
 * - Delete/deactivate shifts (with validation)
 * - Responsive table with accessibility
 */

import { useState, useEffect } from 'react';
import { useShiftsStore } from '@/stores/shiftsStore';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash, Clock } from '@phosphor-icons/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import type { ShiftSchedule } from '@/types/shifts';

// Validation schema
const shiftFormSchema = z.object({
  name: z.string().min(1, 'Shift name is required').max(100, 'Name is too long'),
  start_time: z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format (HH:MM)'),
  end_time: z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format (HH:MM)'),
  description: z.string().max(500, 'Description is too long').optional(),
});

type ShiftFormData = z.infer<typeof shiftFormSchema>;

export function ShiftManagement() {
  const { shifts, loading, error, fetchShifts, createShift, updateShift, deleteShift } = useShiftsStore();
  
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingShift, setEditingShift] = useState<ShiftSchedule | null>(null);
  const [deletingShift, setDeletingShift] = useState<ShiftSchedule | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ShiftFormData>({
    resolver: zodResolver(shiftFormSchema),
  });

  // Fetch shifts on mount and when filter changes
  useEffect(() => {
    fetchShifts(showInactive ? undefined : true);
  }, [showInactive, fetchShifts]);

  // Format time from HH:MM:SS to HH:MM for display
  const formatTime = (time: string) => {
    return time.substring(0, 5); // Remove :SS
  };

  // Handle create shift
  const onCreateSubmit = async (data: ShiftFormData) => {
    try {
      await createShift(data);
      toast.success('Shift created successfully');
      setIsCreateOpen(false);
      reset();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to create shift');
    }
  };

  // Handle edit shift
  const onEditSubmit = async (data: ShiftFormData) => {
    if (!editingShift) return;
    
    try {
      await updateShift(editingShift.id, data);
      toast.success('Shift updated successfully');
      setEditingShift(null);
      reset();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to update shift');
    }
  };

  // Open edit dialog
  const handleEdit = (shift: ShiftSchedule) => {
    setEditingShift(shift);
    reset({
      name: shift.name,
      start_time: formatTime(shift.start_time),
      end_time: formatTime(shift.end_time),
      description: shift.description || '',
    });
  };

  // Handle delete shift
  const handleDelete = async () => {
    if (!deletingShift) return;

    try {
      await deleteShift(deletingShift.id);
      toast.success('Shift deleted successfully');
      setDeletingShift(null);
    } catch (err) {
      toast.error((err as Error).message || 'Failed to delete shift');
    }
  };

  // Toggle shift active status
  const handleToggleActive = async (shift: ShiftSchedule) => {
    try {
      await updateShift(shift.id, { is_active: shift.is_active ? 0 : 1 });
      toast.success(`Shift ${shift.is_active ? 'deactivated' : 'activated'} successfully`);
    } catch (err) {
      toast.error((err as Error).message || 'Failed to update shift status');
    }
  };

  return (
    <div className="space-y-6" role="region" aria-label="Shift Management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Shift Schedules</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage shift templates for automatic checkout
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Show Inactive Toggle */}
          <div className="flex items-center gap-2">
            <Switch
              id="show-inactive"
              checked={showInactive}
              onCheckedChange={setShowInactive}
              aria-label="Show inactive shifts"
            />
            <Label htmlFor="show-inactive" className="cursor-pointer">
              Show inactive
            </Label>
          </div>

          {/* Create Shift Button */}
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" weight="bold" />
                Create Shift
              </Button>
            </DialogTrigger>
            <DialogContent aria-describedby="create-shift-description">
              <DialogHeader>
                <DialogTitle>Create New Shift Schedule</DialogTitle>
                <DialogDescription id="create-shift-description">
                  Define a new shift schedule with start and end times.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit(onCreateSubmit)} className="space-y-4">
                <div>
                  <Label htmlFor="name">Shift Name *</Label>
                  <Input
                    id="name"
                    {...register('name')}
                    placeholder="e.g., Morning Shift"
                    aria-invalid={!!errors.name}
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive mt-1" role="alert">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_time">Start Time *</Label>
                    <Input
                      id="start_time"
                      type="time"
                      {...register('start_time')}
                      placeholder="09:00"
                      aria-invalid={!!errors.start_time}
                    />
                    {errors.start_time && (
                      <p className="text-sm text-destructive mt-1" role="alert">
                        {errors.start_time.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="end_time">End Time *</Label>
                    <Input
                      id="end_time"
                      type="time"
                      {...register('end_time')}
                      placeholder="17:00"
                      aria-invalid={!!errors.end_time}
                    />
                    {errors.end_time && (
                      <p className="text-sm text-destructive mt-1" role="alert">
                        {errors.end_time.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description (optional)</Label>
                  <Textarea
                    id="description"
                    {...register('description')}
                    placeholder="e.g., Standard office hours"
                    rows={3}
                  />
                  {errors.description && (
                    <p className="text-sm text-destructive mt-1" role="alert">
                      {errors.description.message}
                    </p>
                  )}
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsCreateOpen(false);
                      reset();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Creating...' : 'Create Shift'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded" role="alert">
          <p>{error}</p>
        </div>
      )}

      {/* Shifts Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Name</TableHead>
              <TableHead className="w-[120px]">Start Time</TableHead>
              <TableHead className="w-[120px]">End Time</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-[100px]">Status</TableHead>
              <TableHead className="text-right w-[200px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Loading shifts...
                </TableCell>
              </TableRow>
            ) : shifts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No shifts found. Create your first shift to get started.
                </TableCell>
              </TableRow>
            ) : (
              shifts.map((shift) => (
                <TableRow key={shift.id}>
                  <TableCell className="font-medium">{shift.name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      {formatTime(shift.start_time)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      {formatTime(shift.end_time)}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    {shift.description || '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={shift.is_active ? 'default' : 'secondary'}>
                      {shift.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(shift)}
                        aria-label={`Edit ${shift.name}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleActive(shift)}
                        aria-label={`${shift.is_active ? 'Deactivate' : 'Activate'} ${shift.name}`}
                      >
                        {shift.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeletingShift(shift)}
                        aria-label={`Delete ${shift.name}`}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingShift} onOpenChange={() => setEditingShift(null)}>
        <DialogContent aria-describedby="edit-shift-description">
          <DialogHeader>
            <DialogTitle>Edit Shift Schedule</DialogTitle>
            <DialogDescription id="edit-shift-description">
              Update the shift schedule details below.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onEditSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Shift Name *</Label>
              <Input
                id="edit-name"
                {...register('name')}
                aria-invalid={!!errors.name}
              />
              {errors.name && (
                <p className="text-sm text-destructive mt-1" role="alert">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-start">Start Time *</Label>
                <Input
                  id="edit-start"
                  type="time"
                  {...register('start_time')}
                  aria-invalid={!!errors.start_time}
                />
                {errors.start_time && (
                  <p className="text-sm text-destructive mt-1" role="alert">
                    {errors.start_time.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="edit-end">End Time *</Label>
                <Input
                  id="edit-end"
                  type="time"
                  {...register('end_time')}
                  aria-invalid={!!errors.end_time}
                />
                {errors.end_time && (
                  <p className="text-sm text-destructive mt-1" role="alert">
                    {errors.end_time.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="edit-desc">Description</Label>
              <Textarea
                id="edit-desc"
                {...register('description')}
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditingShift(null);
                  reset();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingShift} onOpenChange={() => setDeletingShift(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the shift "{deletingShift?.name}". This action cannot be undone.
              {/* TODO: Check if shift has active assignments and warn user */}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Delete Shift
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
